import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { SuccessToast } from 'components/success-toast';
import ErrorToast from 'components/error-toast';
import { PackageMap } from './package-map';
import { MasterDataPanel } from './master-data-panel';
import { LeftPane } from './left-pane';
import { LeftPaneToggle } from './left-pane-toggle';
import { CenterHeader } from './center-header';
import { WorkspacePage } from './workspace-page';
import { SavedEvalsPage } from './saved-evals-page';
import { ExecutiveSummary } from './executive-summary';
import { PackageOverviewSection } from './package-overview';
import { CashflowsByMonthSection } from './cashflows-by-month';
import { SlotCountSection } from './slot-count';
import { ProductionVolumesSection } from './production-volumes';
import { CashFlowSection } from './cash-flow';
import { InterestModelingSection } from './interest-modeling-section';
import { ValueRollupSection } from './value-rollup-section';
import { ChartsWorkArea } from './custom-chart/charts-work-area';
import { ChartsPersistenceSync } from './custom-chart/charts-persistence-sync';
import { EvalSettingsPanelSection } from './eval-settings-panel';
import { PackageCompareRunsPanel } from './package-compare-runs-panel';
import { ChartExportRegistryProvider } from './chart-export-registry';
import { HeaderActions } from './header-actions';
import {
  usePinResetOnFilterChange,
  useCenterTitle,
  useCalcDetail,
  useScenarios,
  useWorkingConfigForm,
  useEvalRunSync,
  useUnsavedChanges,
  useEvaluationWorkspaces,
} from './hooks';
import { SLOT_VALUATION_INPUTS_QUERY_KEY } from './slot-tearsheet/hooks';
import {
  AgentSurface,
  AgentTriggerButton,
  useAgentDockWidth,
} from 'components/agent-chat';
import { EVAL_AGENT } from 'components/agent-chat/agents/eval-agent';
import {
  applyStagedSettings,
  readStagedChange,
  stagedChangeRefusal,
  unappliedStageWarning,
} from 'components/agent-chat/staged-changes';
import {
  agentChangeToOperation,
  caseLevelStagedSettings,
  evalPendingSettings,
  useCommitAgentOverride,
} from './agent-staged-operations';
import { toSlotOverridesWire } from './utils';
import type { ToolStatus } from 'components/agent-chat/types';
import { useEval3Page } from './context';
import {
  ActivityFeedPanel,
  isActivityFeedPanelOpenAtom,
} from 'components/activity-feed-panel';
import {
  activityFeedModeAtom,
  layoutSettingsAtom,
  leftPaneOpenAtom,
} from './atoms';
import styles from './eval3-new-content.module.scss';

/**
 * Page layout for the Eval 3 master-data page: the left `PageControlsPanel`
 * beside a work area that stacks the Executive Summary, the `PackageMap`, and the
 * `MasterDataPanel`. All three regions are gated on `layoutSettingsAtom` (Layout
 * Settings toggles) — hiding the map lets the table fill the work area, and
 * vice-versa; the Executive Summary is conditionally mounted (see below).
 *
 * The regions are toggled with CSS (`display: none`) rather than conditional
 * mounting, so both stay mounted across toggles. This preserves the map's camera
 * (re-showing returns to the same view, not a re-framed default) and the table's
 * view/search/page state; `PackageMap`'s own container ResizeObserver re-fits the
 * canvas when the map region regains size (mapbox-gl@2's `trackResize` only tracks
 * WINDOW resize, so the map owns container-resize handling itself).
 *
 * This component subscribes only to `layoutSettingsAtom` (a rare, user-driven
 * toggle), not to the pinned-row selection — that atom lives entirely inside
 * `PackageMap`, so a pin click re-renders only the map and never this layout
 * (which would otherwise re-render the heavy panel too).
 *
 * The center is a `CenterHeader` over one always-mounted work area. The route identity
 * (`useEval3Page`) drives what shows: on the browse route (a `workspaceId` with no
 * `:evalId`) the workspace's read-only `WorkspacePage` eval list is prepended to the
 * work-area scroll and the sections below render at PACKAGE level (`calculationId` is
 * genuinely null — no override); on an eval route the sections render eval-level. The
 * right Eval Settings panel is shown in every view (in the browse view it's package mode,
 * and Calculate creates a new eval in the browsed workspace). Eval-URL resolution is done
 * SERVER-SIDE (`Packages::Eval3Controller` 404s a wrong id and redirects a missing one),
 * so the URL reaching the FE is already valid + canonical — this renders straight off the
 * route identity with no client-side fallback logic.
 *
 * @return {JSX.Element} The page layout: left pane + (center header over the body).
 */
export function Eval3NewContent() {
  const layout = useAtomValue(layoutSettingsAtom);
  const centerTitle = useCenterTitle();

  // Output Aggregation stays mounted + rendered OFF-SCREEN when hidden (so its SciChart
  // chart remains capturable). Mark that subtree `inert` while hidden so it leaves the tab
  // order + accessibility tree — unlike the `display:none` regions, an off-screen element is
  // otherwise still focusable/announced. Set via a ref (React 17 doesn't type `inert`).
  const outputAggregationRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (outputAggregationRef.current) {
      outputAggregationRef.current.inert = !layout.outputAggregation;
    }
  }, [layout.outputAggregation]);

  // The working-config form (slot per-cell overrides + Eval Settings inputs) is owned
  // HERE — the page root that stays mounted across the map/table layout toggles and the
  // workspace round-trip — so edits survive `MasterDataPanel`/`SlotView` unmounting, and
  // (Slice C) the Eval Settings panel + slot table share ONE dirty-tracked payload. Seeded
  // from the viewed eval's calc detail (the `useCalcDetail` read dedupes with the other
  // consumers by query key). Passed by prop to `MasterDataPanel` (→ `SlotView`).
  const { calculationId, workspaceId, packageId, isDraft, isSavedEvalsView } =
    useEval3Page();

  // Agent panel for the open workspace. The trigger sits in the header's scoped
  // actions and the dock is padded out of the shell below, so unlike the legacy
  // page there is no scroll position where the toggle becomes unreachable.
  const { data: workspacesData } = useEvaluationWorkspaces(packageId);
  const agentWorkspace =
    workspacesData?.workspaces.find((ws) => ws.id === workspaceId) ?? null;
  // The open run rides along as a query param — eval3-new switches runs by route,
  // but the agent endpoint is workspace-scoped, so the run has to be named.
  const agentMessagesHref = agentWorkspace?.agentMessages?.href
    ? calculationId
      ? `${agentWorkspace.agentMessages.href}?calculation_id=${calculationId}`
      : agentWorkspace.agentMessages.href
    : null;
  const agentDockWidth = useAgentDockWidth(EVAL_AGENT);

  const { data: calcDetail } = useCalcDetail(calculationId);

  // Activity feed — one shared drawer, opened at either scope: package from the left pane's
  // button (beside the package title), calculation from the center header. The mode lives in
  // `activityFeedModeAtom` rather than local state precisely because the two openers sit in
  // different subtrees — a local copy here would ignore the left pane's writes.
  const [activityFeedMode, setActivityFeedMode] = useAtom(activityFeedModeAtom);
  const setActivityFeedOpen = useSetAtom(isActivityFeedPanelOpenAtom);
  const openPackageActivity = useCallback(() => {
    setActivityFeedMode('package');
    setActivityFeedOpen(true);
  }, [setActivityFeedMode, setActivityFeedOpen]);
  const openCalcActivity = useCallback(() => {
    setActivityFeedMode('calculation');
    setActivityFeedOpen(true);
  }, [setActivityFeedMode, setActivityFeedOpen]);
  // A collapsed left pane is marked `inert` (left-pane.tsx), which takes its Package Activity
  // button out of the tab order and the a11y tree — not just out of sight. So the header carries
  // that affordance while the pane is collapsed, and only then: exactly one at any moment.
  const leftPaneOpen = useAtomValue(leftPaneOpenAtom);
  // A from-scratch draft (Model A, the `eval/new` route) seeds the form with today +
  // create-new + the org's default Scenario. Route-derived now: once Calculate routes to
  // the real `eval/:id`, `isDraft` flips false and the form reseeds from the calc detail.
  const { data: scenarios } = useScenarios();
  const workingConfig = useWorkingConfigForm({
    calculationId,
    calcDetail,
    isDraft,
    defaultScenarioId:
      scenarios?.find((scenario) => scenario.isDefault)?.id ?? null,
  });

  // Staged agent changes land as pending override operations — the same channel
  // the Bulk Edit panel appends to, so they show as uncommitted edits and
  // Calculate stays the user's action. The agent has no tool that runs a
  // calculation; nothing here starts one either.
  //
  // Reading the payload lives in `readStagedChange` because this page and the
  // eval-3 workspace parse it identically and only differ in how they apply it.
  // One run-completion toast descriptor; `null` = nothing showing. The embedded `key`
  // increments per event so a REPEAT success/failure re-fires the self-dismissing toast
  // (keying on the message alone would dedupe identical consecutive ones).
  const [runToast, setRunToast] = useState<{
    kind: 'success' | 'error';
    message: string;
    key: number;
  } | null>(null);
  // useCallback so the agent handler's dependency list is stable across renders.
  const showRunToast = useCallback(
    (kind: 'success' | 'error', message: string) =>
      setRunToast((prev) => ({ kind, message, key: (prev?.key ?? 0) + 1 })),
    []
  );

  const queryClient = useQueryClient();

  // Apply a staged operation the same way a grid edit does: paint the loaded rows so the
  // cell reads as overridden, invalidate the surfaces the override touches, then append
  // the operation to the working config and prune the schedule dialog. Lifted into
  // `useCommitAgentOverride` so it can be asserted without mounting this page root — see
  // the note there for what it was missing.
  const { appendSlotOverride, setScheduleSlotOverrides, form } = workingConfig;
  const agentCommitSinks = useMemo(
    () => ({
      appendSlotOverride,
      // Read at commit time, not render time: the agent can stage several changes between
      // renders, and each prune must see the channel the previous one left.
      readScheduleSlotOverrides: () =>
        form.state.values.scheduleSlotOverrides ?? [],
      setScheduleSlotOverrides,
    }),
    [appendSlotOverride, setScheduleSlotOverrides, form]
  );
  const commitAgentOverride = useCommitAgentOverride(
    packageId,
    agentCommitSinks
  );

  // What this page holds that the server has not seen. Only the PENDING log —
  // `committedSlotOverrides` is already in the run's configurations, and sending it twice
  // would have the agent read every saved edit as an unsaved one.
  //
  // Read at send time (the surface calls this), so an edit made while a reply is composing
  // is still described by the state it was sent with rather than a stale render's copy.
  const reportPendingState = useCallback(() => {
    const values = workingConfig.form.state.values;
    const saved = calcDetail;
    // Run-level settings as well as slot overrides. They are not in the operations log,
    // so without this, staging a change of evaluation case left every later tool call
    // reading the old one — deciding against a configuration Calculate would not submit.
    //
    // A diff against the loaded run, for the same reason the overrides are one: these
    // values are hydrated FROM it, so reporting them wholesale would make every
    // untouched run look like it had three pending settings changes.
    // The diff is shared with the other eval page: each had its own copy and they had
    // already drifted on which settings they carried. This page has no cost-source or
    // volume-source picker, so those are left undefined and never appear in the diff.
    // A case's run leaves DCA out (evalPendingSettings).
    const settings = evalPendingSettings(values, saved);

    return {
      reported: true,
      operations: toSlotOverridesWire(values.slotOverrides),
      settings,
    };
  }, [workingConfig, calcDetail]);

  const handleAgentToolResult = useCallback(
    (name: string | null, output: unknown, status: ToolStatus) => {
      const change = readStagedChange(name, output, status);
      if (!change) {
        // A tool that reported `staged: true` and yielded nothing to apply is the silent
        // half-failure this seam exists to catch — say so rather than returning quietly.
        // The console line carries what the parse actually saw, so the cause is one
        // reproduction away instead of a debugging session.
        const unapplied = unappliedStageWarning(name, output, status);
        if (unapplied) {
          console.warn('[agent-chat] staged change not applied', {
            name,
            status,
            output,
          });
          showRunToast('error', unapplied);
        }
        return;
      }

      // Only an exact match on a run that IS open may be applied — see
      // `stagedChangeRefusal`, which both eval pages share so neither can drift into
      // accepting a change staged somewhere else. On a workspace browse route
      // `calculationId` is null while the agent stays mounted, and the server falls back
      // to the latest completed run; committing that into no-run working state confirmed
      // a stage against a run the user was not viewing.
      const refusal = stagedChangeRefusal(change.calculationId, calculationId);
      if (refusal) {
        showRunToast('error', refusal);
        return;
      }

      // Run-level settings are draft fields rather than slot overrides. The
      // operations are ordered so the evaluation case is applied last — it also
      // clears per-area overrides and the deck pick.
      if (change.kind === 'settings') {
        // A case's run: a staged DCA would be dropped by Calculate, so
        // it is not applied, and the user is told (caseLevelStagedSettings).
        const { change: applicable, notice } = caseLevelStagedSettings(
          change,
          workingConfig.form.state.values.evaluationCaseId
        );
        if (notice) showRunToast('error', notice);
        if (applicable) {
          applyStagedSettings(applicable, {
            scenarioId: workingConfig.setScenarioId,
            dcaScenarioId: workingConfig.setDcaScenarioId,
            evaluationCaseId: workingConfig.setEvaluationCase,
          });
        }
        return;
      }

      // Every remaining kind is a per-slot override on the same channel the Bulk
      // Edit panel writes to; the mapping lives in `agentChangeToOperation` so it
      // can be checked without mounting this page.
      commitAgentOverride(agentChangeToOperation(change));
    },
    [calculationId, commitAgentOverride, showRunToast, workingConfig]
  );

  // Always-mounted root: clear the pinned row whenever the dataset filters change
  // (FR#4). Survives hiding the map or the table (unlike PackageMap / the views).
  usePinResetOnFilterChange();

  // Confirm-on-dirty-switch (FR#16): block ANY route change while the working config has
  // uncalculated edits. The hook takes the FORM (not a subscribed boolean) and reads
  // dirtiness off its store AT TRANSITION TIME — so `useCalculate`'s 202 rebaseline (the
  // edits are persisted) lets Calculate's own navigation through in the same tick, and
  // this heavy page root never re-renders on dirty flips at all.
  useUnsavedChanges(
    workingConfig.form,
    calcDetail?.configurations?.interest_items
  );

  // Run lifecycle — the sole Pusher subscriber lives here (the form owner) so completion
  // can rebaseline the form. On success: reset the form to the just-calculated values
  // (clears dirty → Calculate disables) + a success toast. On a BE error: surface it and
  // KEEP the form dirty so the user can fix and retry. The subscribe/invalidate + the
  // Package Overview refresh happen inside the hook.
  // The error toast is PERSISTENT (a failed run is a standing state), so it must not
  // outlive its eval: leaving for another eval drops it — and `useEvalRunSync` re-fires
  // `onRunFailed` on every arrival at an errored eval, so coming back re-shows it.
  useEffect(() => {
    setRunToast(null);
  }, [calculationId]);
  // Per-eval chart layout is now persisted + rehydrated (not cleared) on eval switch — the
  // headless `ChartsPersistenceSync` (mounted below) hydrates `chartsAtom`/`chartGridAtom`
  // from `CalcDetail.chartConfig` and autosaves changes. Its atom subscriptions live in that
  // null node, so chart-state churn never re-renders this heavy root.
  useEvalRunSync({
    onRunCompleted: () => {
      // ── THE TEARSHEET'S "FROZEN BLOB" ASSUMPTION DOES NOT SURVIVE AN IN-PLACE RE-RUN ──
      //
      // `useSlotValuationInputs` holds its payload at `staleTime: Infinity`, on the stated
      // reasoning that "per-slot payload is stable within a completed calc (the blob is
      // frozen)". That is true for a fork, which lands on a NEW calculationId and therefore a
      // new query key. It is FALSE for an update-in-place re-run — the default path
      // (`createNewEvalOnCalculate: false`, utils.tsx) — which keeps the id and replaces the
      // blob underneath it. Without this the tearsheet serves the PREVIOUS run's values
      // forever: the numbers are pre-run and every "Override pending" badge still claims the
      // override has not applied when it now has. On a page whose whole purpose is
      // scrutability, that is the worst available failure, and it is silent.
      //
      // Invalidated at the `[key, packageId]` prefix — the same prefix `commitSlotOverride`
      // uses — so every cached slot's tearsheet is covered, not just the one last opened.
      //
      // WHY HERE AND NOT IN useEvalRunSync'S OWN LOOP: slot-tearsheet/hooks imports
      // `useCalcLifecycle` from eval3-new/hooks, so importing its query key back into that
      // module would close an import cycle. This is the hook's own completion callback, so
      // it is still the one run-completion path rather than a second one.
      void queryClient.invalidateQueries({
        queryKey: [SLOT_VALUATION_INPUTS_QUERY_KEY, packageId],
      });
      // Rebaseline to the just-calculated values → clears dirty so Calculate disables.
      // (v1 doesn't lock inputs mid-run, so an edit made WHILE calculating is folded into
      // this baseline — an accepted happy-path limitation; see deferred-items.md.)
      // INS-10777 — clear the pending operations on rebaseline: they're now committed
      // (persisted in the calc config + resolved into the read), so re-sending them as
      // pending would double-apply and grow the list unboundedly across runs.
      workingConfig.form.reset({
        ...workingConfig.form.state.values,
        slotOverrides: [],
      });
      showRunToast('success', 'Calculation complete.');
    },
    // Surface the BE error; the form stays dirty (NOT rebaselined) so the user can
    // retry. Fires on the in-session failure AND on arriving at an already-failed eval
    // (switch / reload) — the one announcement path for the error state.
    onRunFailed: (message) => showRunToast('error', message),
  });

  // Browse view = a workspace route with NO eval and NOT a draft: the folder's eval-list
  // table sits atop the package-level sections. A draft (`eval/new`) also has a workspace
  // + null calc, but it's the compose view — package-mode work area + panel, no table. An
  // eval route (calc set) shows just the eval.
  const showBrowseTable =
    calculationId === null && workspaceId !== null && !isDraft;
  // The Saved Evaluations browse view (the pinned folder's route) — its table prepends above
  // the package-level sections, exactly like the workspace-browse table.
  const showSavedEvals = isSavedEvalsView;

  return (
    <ChartExportRegistryProvider>
      <div className={styles.page}>
        {/* The dock is `position: fixed`; the shell is a fixed-height flex layout that would
            otherwise render underneath it. Padding reserves the width instead, so the work area
            shrinks rather than hiding behind the panel. */}
        <div
          className={styles.shell}
          style={agentDockWidth ? { paddingRight: agentDockWidth } : undefined}
        >
          {/* Headless: hydrates + autosaves the per-eval chart layout. Renders null; its chart
          atom subscriptions live inside it so chart churn doesn't re-render this root. */}
          <ChartsPersistenceSync />
          <LeftPane />
          <div className={styles.center}>
            <CenterHeader
              title={centerTitle.title}
              editable={centerTitle.editable}
              label={centerTitle.label}
              onSave={centerTitle.onSave}
              saving={centerTitle.saving}
              saveError={centerTitle.saveError}
              // Far-left toggle collapsing/expanding the left pane (mirrors the right panel).
              leading={<LeftPaneToggle />}
              // Scoped actions (mock §1): the agent trigger, the activity buttons, the "Actions"
              // menu, the Eval Settings toggle, and the primary Save action. See `HeaderActions`
              // for the composition + Save gate. Package activity lives in the left pane while
              // it's open, and moves here when it's collapsed.
              actions={
                <HeaderActions
                  workingConfig={workingConfig}
                  onOpenPackageActivity={
                    leftPaneOpen ? undefined : openPackageActivity
                  }
                  onOpenCalcActivity={
                    calculationId !== null ? openCalcActivity : undefined
                  }
                  agentTrigger={
                    agentMessagesHref ? (
                      // `md` to match the Actions / Eval Settings / Save cluster it sits
                      // in — the trigger's own default is `sm`, which is the height every
                      // other host wants and is a visible step shorter than these.
                      <AgentTriggerButton agent={EVAL_AGENT} size="md" />
                    ) : undefined
                  }
                />
              }
            />
            {/* ONE vertical scroll: on the browse route the workspace's eval-list table sits
            ABOVE the (package-level) sections; on an eval route it's just the eval work
            area. Always mounted (map camera + table state survive layout toggles). */}
            <div className={styles.workArea}>
              {/* Workspace eval-list table (mock §6) — browse route only; rendered on the
              route identity so its row clicks route to the right eval. */}
              {showBrowseTable && workspaceId !== null && (
                <WorkspacePage workspaceId={workspaceId} />
              )}
              {/* Saved Evaluations browse (the pinned folder) — prepended above the package
              sections, mirroring the workspace-browse table. */}
              {showSavedEvals && <SavedEvalsPage />}
              {/* Executive Summary — gated on layout.executiveSummary. Unlike the map +
              table (kept mounted via CSS so their camera / scroll / page state
              survives a toggle), this is conditionally mounted: it has no transient
              UI state to preserve, and unmounting avoids fetching the summary while
              it's hidden. Its RQ data is cached, so re-showing is an instant cache
              hit, not a refetch flash. */}
              {layout.executiveSummary && <ExecutiveSummary />}
              {/* Map + table are ONE Master Data card (mock §6): stacked flush with no
              gap between them — only the work area's 24px gap separates this card
              from the Executive Summary above. */}
              <div className={styles.masterDataPanel}>
                <div
                  className={classNames(styles.mapRegion, {
                    [styles.hidden]: !layout.map,
                  })}
                >
                  <PackageMap />
                </div>
                <div
                  className={classNames(styles.panelRegion, {
                    [styles.hidden]: !layout.slotAllocationsTable,
                  })}
                >
                  <MasterDataPanel workingConfig={workingConfig} />
                </div>
              </div>
              {/* Package Overview output section (P5) — gated on layout.packageOverview.
              Conditionally mounted like the Executive Summary (no transient UI state;
              RQ-cached, so re-showing is a cache hit). Sits below the Master Data card
              as an output section. */}
              {layout.packageOverview && <PackageOverviewSection />}
              {/* Cashflows-by-Month output section — same gating/mount posture. */}
              {layout.cashflowsByMonth && <CashflowsByMonthSection />}
              {/* Slot Count output section (reserve-category roll-forward chart + table).
              UNLIKE the sibling output sections (conditionally mounted), this one is ALWAYS
              mounted and CSS-hidden when its Layout toggle is off — the deliberate exception
              that keeps its Reserves chart registered + capturable for content export even
              while hidden (P8 FR9: Reserves always-exports). Highcharts captures offscreen
              from the chart's options, so a display:none chart still exports. */}
              <div
                className={classNames({ [styles.hidden]: !layout.slotCount })}
              >
                <SlotCountSection />
              </div>
              {/* Production Volumes output section (aggregated production plot). Like Slot
              Count (and unlike the conditionally-mounted sections), this one is ALWAYS
              mounted and CSS-hidden when its Layout toggle is off — the exception that keeps
              its Highcharts chart registered + capturable for content export even while
              hidden (Highcharts captures offscreen from the chart's options). */}
              <div
                className={classNames({
                  [styles.hidden]: !layout.productionVolumes,
                })}
              >
                <ProductionVolumesSection />
              </div>
              {/* Cash Flow output section (aggregated free-cash-flow chart + cumulative table).
              Like Slot Count / Production Volumes (and unlike the conditionally-mounted
              sections), this one is ALWAYS mounted and CSS-hidden when its Layout toggle is
              off — the exception that keeps its Highcharts chart registered + capturable for
              content export even while hidden (Highcharts captures offscreen from the chart's
              options). */}
              <div
                className={classNames({
                  [styles.hidden]: !layout.cashFlow,
                })}
              >
                <CashFlowSection />
              </div>
              {/* Output Aggregation (ValueRollup). Unlike the sections above (display:none when
              hidden), this one is rendered OFF-SCREEN when its toggle is off: its Visual Summary
              is a SciChart chart, and SciChart captures blank when display:none — so it must stay
              laid out + rendered to remain registered/capturable for content export. */}
              <div
                ref={outputAggregationRef}
                className={classNames({
                  [styles.offscreen]: !layout.outputAggregation,
                })}
              >
                <ValueRollupSection />
              </div>
              {/* Interest Modeling (INS-10673) — the Assets frame: the
              position tree + per-kind editor/results placeholders. The position
              list itself lives in the working config (`interestItems`), so it
              survives a Layout Settings toggle regardless; kept mounted + CSS-hidden
              (like Slot Count) so the section's own ephemeral view state (selection,
              open node, panel toggle) also survives one.

              Placed after the fixed output sections and BEFORE Charts & Visuals,
              matching the mock (App.tsx: CarriedInterestExplorer renders after
              ACCORDION_SECTIONS, then the added-charts block). Charts & Visuals
              renders nothing until a chart is added, so in the default state this
              is the bottom-most section.

              `key` on the page identity resets that ephemeral view state when the
              user navigates to a different package/eval — this page component is
              reused across route-param changes (no remount), so without the key one
              eval's selection/open-node would leak into the next. The key is stable
              across a Layout Settings toggle (identity unchanged). */}
              <div
                className={classNames({
                  [styles.hidden]: !layout.interestModeling,
                })}
              >
                <InterestModelingSection
                  key={`interest-modeling-${packageId}-${calculationId}`}
                  workingConfig={workingConfig}
                />
              </div>
              {/* Charts & Visuals output section — isolated in its own component so chart-state
              churn (add / remove / toggle / rename / grid dimensions) re-renders only that
              subtree, not this feature root. Renders nothing until a chart is visible, and
              gated on layout.chartsVisuals so the Layout Settings toggle can hide the whole
              section (parity with the other output sections). */}
              {layout.chartsVisuals && <ChartsWorkArea />}
              {/* Package Compare runs (not a layout-toggled output section): lists the
              package's Analyze-Value-Drivers runs. Sits at the bottom of the section
              stack — in the default layout directly below Interest Modeling, since
              Charts & Visuals renders nothing until a chart is added. Renders nothing
              until the package has a run, so a never-compared package is unchanged.
              (Start a new comparison via Actions › Package Compare.) */}
              <PackageCompareRunsPanel />
            </div>
          </div>
          {/* Right Eval Settings column — connected: owns the panel-open atom + the Calculate
          gate, so the form-state subscriptions re-render only the panel subtree, not this
          page root (map / table / summary). */}
          <EvalSettingsPanelSection workingConfig={workingConfig} />
          {/* Run toast (portaled to body): success auto-dismisses (a moment, not a state);
          the failure toast is PERSISTENT (timeout 0 — stays until the user closes it)
          because a failed run is a standing state, announced on the in-session failure
          and on every arrival at an errored eval. Cleared on eval switch (above). */}
          {runToast &&
            (runToast.kind === 'success' ? (
              // 10s (over the 5s default): a run can finish while the user is heads-down
              // in the table/map — give the announcement a longer window to be seen.
              <SuccessToast
                key={runToast.key}
                message={runToast.message}
                timeout={10_000}
              />
            ) : (
              <ErrorToast
                key={runToast.key}
                message={runToast.message}
                timeout={0}
                // Manual dismissal clears the descriptor too, so React state (not
                // Carbon's internal node state) stays the one owner of visibility.
                // `true` = let Carbon's default hide proceed.
                onClose={() => {
                  setRunToast(null);
                  return true;
                }}
              />
            ))}

          <ActivityFeedPanel
            modelType={
              activityFeedMode === 'calculation' && calculationId
                ? 'EvaluationCalculation'
                : 'EvaluationPackageFeed'
            }
            modelId={
              activityFeedMode === 'calculation' && calculationId
                ? calculationId
                : packageId
            }
            title={
              activityFeedMode === 'calculation'
                ? 'Evaluation Activity'
                : 'Package Activity'
            }
            commentsHref={
              activityFeedMode === 'calculation' && calculationId
                ? `/evaluation_calculations/${calculationId}/comments`
                : `/packages/${packageId}/comments`
            }
          />
          {agentMessagesHref && agentWorkspace && (
            <AgentSurface
              agent={EVAL_AGENT}
              agentMessagesHref={agentMessagesHref}
              subject={{ type: 'EvaluationWorkspace', id: agentWorkspace.id }}
              subtitle={`Ask about ${agentWorkspace.name}`}
              model={agentWorkspace.agentMessages?.model}
              onToolResult={handleAgentToolResult}
              pendingState={reportPendingState}
            />
          )}
        </div>
      </div>
    </ChartExportRegistryProvider>
  );
}
